import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Card, Result, Spin, theme } from "antd";

import { apiErrorMessage } from "@/lib/errors";
import { verifyEmail } from "./api";

type State = "verifying" | "ok" | "error";

/** Confirm an email address from the token in the verification link. */
function VerifyEmailPage() {
  const { token } = theme.useToken();
  const [params] = useSearchParams();
  const verifyToken = params.get("token") ?? "";
  const [state, setState] = useState<State>("verifying");
  const [error, setError] = useState<string>("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 strict double-invoke
    ran.current = true;
    if (!verifyToken) {
      setState("error");
      setError("This verification link is missing its token.");
      return;
    }
    verifyEmail(verifyToken)
      .then(() => setState("ok"))
      .catch((e) => {
        setError(apiErrorMessage(e, "Could not verify your email"));
        setState("error");
      });
  }, [verifyToken]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: token.colorBgLayout,
      }}
    >
      <Card style={{ width: 460 }}>
        {state === "verifying" && (
          <Result icon={<Spin size="large" />} title="Verifying your email…" />
        )}
        {state === "ok" && (
          <Result
            status="success"
            title="Email verified"
            subTitle="Thanks — your account is all set."
            extra={
              <Link to="/">
                <Button type="primary">Go to dashboard</Button>
              </Link>
            }
          />
        )}
        {state === "error" && (
          <Result
            status="error"
            title="Verification failed"
            subTitle={error}
            extra={
              <Link to="/">
                <Button>Back to OmniCloud</Button>
              </Link>
            }
          />
        )}
      </Card>
    </div>
  );
}

export default VerifyEmailPage;
